import type { ApolloLink } from '@apollo/client'
import type React from 'react'
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { ModelPermission } from 'src/components/GenerationModel/constants'
import type { UpsertGenerationModelMutation } from 'src/services/apollo/generated/graphql'
import type {
  GenerationModelType,
  Media,
  Tag,
} from 'src/services/apollo/generated/graphql'
import { executeUpsertGenerationModel } from 'src/services/apollo/generation-model'
import {
  getCreateModelInputs,
  getSubmitModelTrackAttrs,
} from 'src/services/helpers/modelApiParamsPacker'
import type { QualityTag } from 'src/utils/model'
import { track } from 'src/utils/tracker'
import {
  createHandleChange,
  type HandleChange,
  useForm,
} from 'src/utils/useForm'
import { type TagItem } from '~/components/Tag/utils'
import { ExternalModelImporterContext } from './context'

const ModelFormSubmitContext = createContext<{
  submit: () =>
    | Promise<ApolloLink.Result<UpsertGenerationModelMutation> | undefined>
    | undefined
  values: ModelSubmitFormValues
  handleChange: HandleChange<ModelSubmitFormValues>
  getValues: () => Promise<ModelSubmitFormValues> | undefined
  setValues: Dispatch<SetStateAction<ModelSubmitFormValues>>
}>({
  submit: () => undefined,
  values: {},
  /**
   * Only used for initialization, it lacks real handleChange functionality;
   * the actual handleChangeHandler is initialized at line 155 in this file, inside useForm hook.
   */
  handleChange: createHandleChange(),
  getValues: () => undefined,
  setValues: () => {},
})
ModelFormSubmitContext.displayName = 'ModelFormSubmitContext'

export const useModelSubmitForm = () => useContext(ModelFormSubmitContext)

export const modelSamplingDiscreteSamplingTypes = [
  'eps',
  'v_prediction',
  'lcm',
  'x0',
] as const
export type ModelSamplingDiscreteSamplingType =
  (typeof modelSamplingDiscreteSamplingTypes)[number]
export interface ModelSamplingInputs {
  ModelSamplingDiscrete: {
    sampling: ModelSamplingDiscreteSamplingType
    zsnr: boolean
  }
  ModelSamplingSD3: {
    shift: number
  }
  ModelSamplingFlux: {
    base_shift: number
    max_shift: number
    height: number
    width: number
  }
}

export type ModelSampling<
  T extends keyof ModelSamplingInputs = keyof ModelSamplingInputs,
> = {
  type: T
  inputs: ModelSamplingInputs[T]
}

export interface InferenceConfig {
  modelSampling?: ModelSampling
  rescaleCFG?: number
}

export type VersionValues = {
  // equals to version.name
  name?: string
  // for loras
  triggerWords?: string
  // for sdv1models
  negativePrompts?: string
  samplingMethod?: string
  samplingSteps?: number
  cfgScale?: number
  previewArtworkIds?: string[]
  inferenceConfig?: InferenceConfig
  qualityTags?: QualityTag[]
}

export type ModelSubmitFormValues = Partial<{
  // display only
  uploadProgress?: 0

  // have to agree before proceeding to step 2
  agreeNonMinor?: boolean
  agreeCoverImage?: boolean

  // for editing
  media: Media
  downloadUrl: string
  modelId: string
  editFilename: string

  firstVersionExtra: VersionValues
  uploadId: string
  title: string
  tags: TagItem[]
  type: GenerationModelType
  category: string
  isDownloadable: boolean
  isPrivate: boolean
  // for DIT LoRA download restriction display
  loraBaseModelTypes: GenerationModelType[]
  extra: {
    description?: string
    qualityTags?: QualityTag[]
    permissions?: {
      [key in ModelPermission]: boolean
    }

    /** below fields are not visible to user */
    challenge?: string
    recognizedNsfw?: boolean
  }
}>

const defaultValue: ModelSubmitFormValues = {
  extra: {
    permissions: {
      // TODO: Cleanup next month
      // Other model permissions except commercialUses are removed on 2025-11-27

      // shareImagesOnline: true,
      // personalUses: true,
      commercialUses: true,
      // shouldCreditAuthor: false,
    },
  },
}

export default function ModelSubmitFormProvider(
  props: React.PropsWithChildren<{
    initialValues?: ModelSubmitFormValues
  }>,
) {
  const { t } = useTranslation(['market'])
  const form = useForm<ModelSubmitFormValues>(
    props.initialValues ?? defaultValue,
  )
  const { values, setValues } = form

  // Sync initialValues.tags to form.values only once during initialization
  // This is a workaround to ensure that the tags are properly synced to the form.values
  useEffect(() => {
    if (
      props.initialValues?.tags &&
      props.initialValues.tags.length > 0 &&
      (!values.tags || values.tags.length === 0)
    ) {
      setValues(prev => ({ ...prev, tags: props.initialValues?.tags }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { url } = useContext(ExternalModelImporterContext)
  const submit = useCallback(async () => {
    const baseValues = values

    const submitInput = getCreateModelInputs(t, baseValues)

    const res = await executeUpsertGenerationModel({
      id: values.modelId,
      input: submitInput,
    })

    track('submit_model_success', {
      ...getSubmitModelTrackAttrs({ values: baseValues }),
      url,
    })
    return res
  }, [url, values])

  const ctxValue = useMemo(() => {
    return {
      submit,
      ...form,
    }
  }, [submit, form])

  return (
    <ModelFormSubmitContext.Provider value={ctxValue}>
      {props.children}
    </ModelFormSubmitContext.Provider>
  )
}
